import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { CircularProgress, Divider } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { ActionsPopper } from 'common/components/ActionsPopper';
import { setCurrentEntityType, setDialogOpen } from 'common/components/deleteDialog/deleteDialogSlice';
import { openDrawer, setCurrentDrawerId, setCurrentEntity, setCurrentOperation } from 'common/components/EditDrawer/editDrawerSlice';
import { Roles } from 'common/constants/roles';
import { Routes } from 'common/constants/routes';
import { usePalmButtonEvaluator } from 'common/hooks/usePalmButtonEvaluator';
import { extractStatusCode, is404Error } from 'common/services/api/apiHelpers';
import { fetchTenants } from 'common/services/api/Tenants/Tenants.thunks';
import { fetchTenantUserMemberships } from 'common/services/api/TenantUserMembership/TenantUserMembership.thunk';
import { UserTenantMembershipStatusProps } from 'common/services/api/TenantUserMembership/TenantUserMembershipApiTypeDefs';
import { fetchUserProfileApps } from 'common/services/api/UserProfileApps/UserProfileApps.thunk';
import { UserProfileApps as UserProfileAppsType } from 'common/types/Entities';
import { DrawerEntitiesNames, Operations } from 'common/types/TypeDefs';
import { DrawerIDs } from 'common/utils/DrawerIDs';
import { handleListFetchError } from 'common/utils/errorHandlers';
import { clearUserProfileAcls } from 'features/userProfileAcls/userProfileAclsSlice';
import { clearUserProfileClaims } from 'features/userProfileClaims/userProfileClaimsSlice';
import { clearUserProfileGlobalRoles } from 'features/userProfileGlobalRoles/userProfileGlobalRolesSlice';
import { clearUserProfileRoles } from 'features/userProfileRoles/userProfileRolesSlice';
import { clearUserProfileUserGroups } from 'features/userProfileUserGroups/userProfileUserGroupsSlice';
import { useSnackbar } from 'notistack';

import { UserProfileAppsHeaderSection } from './UserProfileAppsHeaderSection';
import { UserProfileAppsTenants } from './UserProfileAppsTenants';
import { UserProfileAppsTenantsDeleteDialog } from './UserProfileAppsTenantsDeleteDialog';
import { UserProfileAppsTenantsEditDrawer } from './UserProfileAppsTenantsEditDrawer';
import { selectProcessing, selectUserProfileAppsTenants, setProcessing,setUserProfileAppsTenants } from './UserProfileAppsTenantsSlice';

interface IUserProfileAppsProps {
    title: string;
}

interface IUserTenantMemberships extends UserTenantMembershipStatusProps {
    isActive: boolean;
    notes: string;
    tenantId: string;
    tenantName: string;
    userId: string;
}

export interface IUserProfileTenant extends UserTenantMembershipStatusProps {
    id: string;
    applicationSuiteId: string;
    name: string;
    isActive?: boolean;
    notes?: string;
    isLicenseRequired: boolean;
}

export interface IUserAppSuiteAndTenants {
    appSuiteName: string;
    appSuiteId: string;
    tenants: IUserProfileTenant[];
}

const APP_SUITE_LINK_TENANT_BUTTON_TITLE = '+ Link Tenant';

const buildAppSuiteTenantsResult = (
    tenantsbyId: IUserProfileTenant[][],
    userProfileApps: UserProfileAppsType[]
): IUserAppSuiteAndTenants[] => {
    return tenantsbyId.reduce<IUserAppSuiteAndTenants[]>((result, tenants) => {
        const applicationSuiteId = tenants[0]?.applicationSuiteId;
        const userProfileApp = userProfileApps.find(item => item.id === applicationSuiteId);
        if (userProfileApp) {
            result.push({
                appSuiteId: userProfileApp.id,
                appSuiteName: userProfileApp.name,
                tenants,
            });
        }
        return result;
    }, []);
};

const mergeTenantWithMembership = (tenant: IUserProfileTenant, membership: IUserTenantMemberships): void => {
    tenant.isActive = membership.isActive;
    tenant.notes = membership.notes;
    tenant.startDate = membership.startDate;
    tenant.endDate = membership.endDate;
    tenant.status = membership.status;
    tenant.aclCounts = membership.aclCounts;
    tenant.globalRoleCounts = membership.globalRoleCounts;
    tenant.roleCounts = membership.roleCounts;
    tenant.claimCounts = membership.claimCounts;
};

export const UserProfileApps = (props: IUserProfileAppsProps)=> {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [editingRow, setEditingRow] = useState<{
        appSuiteName: string;
        tenant: IUserProfileTenant;
        startDate: string;
        endDate?: string;
    } | null>(null);
    const [shouldDisableDeleteButton, setShouldDisableDeleteButton] = useState<boolean>(false);
    const userProfileAppsTenants = useAppSelector(selectUserProfileAppsTenants);
    const processing = useAppSelector(selectProcessing);
    const { title } = props;
    const params = useParams();
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const shouldDisableLinkButton = usePalmButtonEvaluator([ Roles.UserManager, Roles.Admin ]);
    const shouldDisableEditButton = usePalmButtonEvaluator([ Roles.UserManager, Roles.Admin, Roles.GlobalRoleManager ]);
    const shouldDisableOpenButton = usePalmButtonEvaluator([ Roles.UserManager, Roles.Admin, Roles.AppManager, Roles.MultiTenantRO ]);
    const userIsNotAdmin = usePalmButtonEvaluator([ Roles.Admin ]);
    const userIsNotUserAndGlobalManager = usePalmButtonEvaluator([ Roles.UserManager, Roles.GlobalRoleManager ], true);
    const userIsNotUserManager = usePalmButtonEvaluator([ Roles.UserManager ]);

    const handleFetchProfileAppsPage = () => {
        dispatch(setProcessing(true));
        const requestParams = {
            userId: params?.userId ?? ''
        };

        Promise.all([
            dispatch(fetchUserProfileApps(requestParams)).unwrap(),
            dispatch(fetchTenantUserMemberships(requestParams)).unwrap()
        ])
            .then(handleFetchTenantsByApps)
            .catch(error => {
                if(is404Error(error.message)) {
                    dispatch(setUserProfileAppsTenants([]));
                    dispatch(setProcessing(false));
                } else {
                    handleListFetchError(extractStatusCode(error.message), 'Apps', enqueueSnackbar);
                    dispatch(setProcessing(false));
                }
            });
    };

    const handleFetchTenantsByApps = async ([userProfileApps, userTenantMemberships]: [UserProfileAppsType[], IUserTenantMemberships[]]) => {
        const appSuiteTenantsPromise = userProfileApps.map(item => dispatch(fetchTenants({ id: item.id })).unwrap());
        try {
            const tenantsbyId = await Promise.all(appSuiteTenantsPromise);
            const processedResult = buildAppSuiteTenantsResult(tenantsbyId, userProfileApps);
            handleComputeUserProfileAppsTenants(userTenantMemberships, processedResult);
        } catch (error) {
            handleListFetchError(extractStatusCode((error as Error).message), 'Apps', enqueueSnackbar);
            dispatch(setProcessing(false));
        }
    };

    const handleComputeUserProfileAppsTenants = (userTenants: IUserTenantMemberships[], appSuiteTenants: IUserAppSuiteAndTenants[]) => {
        const userTenantMap = new Map(userTenants.map(ut => [ut.tenantId, ut]));

        const commons = appSuiteTenants.map(({ appSuiteName, appSuiteId, tenants }) => {
            const matchedTenants = tenants.filter(tenant => userTenantMap.has(tenant.id));
            for (const tenant of matchedTenants) {
                const membership = userTenantMap.get(tenant.id);
                if (membership) {
                    mergeTenantWithMembership(tenant, membership);
                }
            }
            return { appSuiteName, appSuiteId, tenants: matchedTenants };
        });

        dispatch(setUserProfileAppsTenants(commons));
        dispatch(setProcessing(false));
    };

    useEffect(handleFetchProfileAppsPage, []);

    useEffect(() => () => {
        dispatch(clearUserProfileGlobalRoles());
        dispatch(clearUserProfileUserGroups());
        dispatch(clearUserProfileRoles());
        dispatch(clearUserProfileClaims());
        dispatch(clearUserProfileAcls());
    }, []);

    const handleClosePopper = () => {
        setEditingRow(null);
        setAnchorEl(null);
    };

    const handleOpenActionsMenu = (row: {
        appSuiteName: string;
        tenant: IUserProfileTenant;
        startDate: string;
        endDate?: string;
    }) => (event: React.MouseEvent<HTMLElement>) => {
        if (
            row.tenant.id === editingRow?.tenant.id &&
            row.tenant.applicationSuiteId === editingRow?.tenant.applicationSuiteId &&
            anchorEl
        ) {
            handleClosePopper();
        } else {
            if (row.tenant.globalRoleCounts == 0) {
                setShouldDisableDeleteButton(userIsNotUserManager && userIsNotAdmin);
            } else {
                setShouldDisableDeleteButton(userIsNotUserAndGlobalManager && userIsNotAdmin);
            }
            setEditingRow(row);
            setAnchorEl(event.currentTarget);
        }
        event.stopPropagation();
    };

    const handleCreateButtonClick = () => {
        dispatch(setCurrentOperation(Operations.Link));
        dispatch(setCurrentEntity(DrawerEntitiesNames.UserTenant));
        dispatch(setCurrentDrawerId(DrawerIDs.UserProfileAppTenants));
        dispatch(openDrawer());
    };

    const handleEditButtonClick = () => {
        dispatch(setCurrentOperation(Operations.Edit));
        dispatch(setCurrentEntity(DrawerEntitiesNames.UserTenant));
        dispatch(setCurrentDrawerId(DrawerIDs.UserProfileAppTenants));
        dispatch(openDrawer());
        setAnchorEl(null);
    };

    const handleDeleteButtonClick = () => {
        dispatch(setDialogOpen(true));
        dispatch(setCurrentEntityType(DrawerEntitiesNames.UserTenant));
        setAnchorEl(null);
    };

    const handleSetEditingRow = (row: {
        appSuiteName: string;
        tenant: IUserProfileTenant;
        startDate: string;
        endDate?: string;
    } | null) => () => {
        setEditingRow(row);
    };

    const getAppSuiteUrl = () => {
        if (!editingRow) {
            return null;
        }
        return `/${Routes.ApplicationSuites}/${editingRow.tenant.applicationSuiteId}/${Routes.Tenants}/${editingRow.tenant.id}/${Routes.Users}`;
    };

    return (
        <div>
            <Divider sx={{ marginTop: '2rem', fontWeight: 'bold' }} />
            <UserProfileAppsHeaderSection
                title={title}
                createButtonClickHandler={handleCreateButtonClick}
                shouldDisableLinkButton={shouldDisableLinkButton}
                createButtonTitle={APP_SUITE_LINK_TENANT_BUTTON_TITLE}
            />
            <section>
                {
                    processing ? <CircularProgress color='primary'/> :
                        userProfileAppsTenants.map((item) => (
                            <UserProfileAppsTenants
                                appSuite={item}
                                key={item.appSuiteId}
                                handleOpenActionsMenu={handleOpenActionsMenu}
                                handleSetEditingRow={handleSetEditingRow}
                            />
                        ))
                }
            </section>
            <UserProfileAppsTenantsEditDrawer
                editingRow={editingRow}
                updatePageCallback={handleFetchProfileAppsPage}
                handleClose={handleSetEditingRow(null)}
            />
            <UserProfileAppsTenantsDeleteDialog
                editingRow={editingRow}
                updatePageCallback={handleFetchProfileAppsPage}
            />
            <ActionsPopper
                anchorEl={anchorEl}
                handleClosePopper={handleClosePopper}
                actions={[
                    { operation: Operations.Open, disabled: shouldDisableOpenButton, urlToOpen: getAppSuiteUrl },
                    { operation: Operations.Edit, handleOperation: handleEditButtonClick, disabled: shouldDisableEditButton },
                    { operation: Operations.Delete, handleOperation: handleDeleteButtonClick, disabled: shouldDisableDeleteButton },
                ]}
            />
        </div>
    );
};
